<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { SearchCombobox, FilterModal, ThemeProvider } from '@/shared/ui';
  import { positionApi, type Position } from '@/entities/Position';
  import type { MemberTrainingStatus, TrainingCollaboratorFilters } from '@/entities/Training';

  interface StatusOption {
    code: MemberTrainingStatus;
    label: string;
  }

  const STATUS_OPTIONS: StatusOption[] = [
    { code: 'PENDING', label: 'Pendente' },
    { code: 'COMPLETED', label: 'Concluído' },
    { code: 'NOT_COMPLETED', label: 'Não concluído' },
  ];

  const props = defineProps<{
    currentFilters?: TrainingCollaboratorFilters;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: TrainingCollaboratorFilters];
    'clear-filters': [];
  }>();

  const positions = ref<Position[]>([]);
  const isPositionsLoading = ref(false);
  const positionSearchQuery = ref('');

  const positionOptions = computed(() => {
    const query = positionSearchQuery.value.trim().toLowerCase();
    return positions.value
      .filter((position) => !query || position.name.toLowerCase().includes(query))
      .map((position) => ({ value: String(position.id), label: position.name }));
  });

  function handlePositionSearch(value: string) {
    positionSearchQuery.value = value;
  }

  const draftPositionId = ref('');
  const draftStatus = ref<MemberTrainingStatus | undefined>(undefined);

  function loadFromFilters(filters?: TrainingCollaboratorFilters) {
    draftPositionId.value = filters?.positionId ? String(filters.positionId) : '';
    draftStatus.value = filters?.status;
  }

  async function loadPositions() {
    isPositionsLoading.value = true;
    try {
      const response = await positionApi.getAllPositions({ itemsPerPage: 1000 });
      positions.value = response.data || [];
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
    } finally {
      isPositionsLoading.value = false;
    }
  }

  onMounted(async () => {
    await loadPositions();
    loadFromFilters(props.currentFilters);
  });

  watch(
    () => props.currentFilters,
    (filters) => loadFromFilters(filters),
    { deep: true }
  );

  function toggleStatus(code: MemberTrainingStatus) {
    draftStatus.value = draftStatus.value === code ? undefined : code;
  }

  function handleApply() {
    emit('update:filter', {
      status: draftStatus.value,
      positionId: draftPositionId.value ? Number(draftPositionId.value) : undefined,
    });
  }

  function handleClear() {
    draftPositionId.value = '';
    draftStatus.value = undefined;
    positionSearchQuery.value = '';
  }
</script>

<template>
  <FilterModal
    content-class="w-auto max-w-md"
    @close="emit('close')"
    @apply-filters="handleApply"
    @clear-filters="handleClear"
  >
    <template #content>
      <ThemeProvider>
        <div class="p-3">
          <div class="p-6 space-y-6">
            <div>
              <h4 class="font-medium text-gray-07 pb-4">
                Status
              </h4>
              <div
                data-testid="training-collaborators-status-options"
                class="flex flex-col items-start gap-2"
              >
                <button
                  v-for="option in STATUS_OPTIONS"
                  :key="option.code"
                  type="button"
                  :data-testid="`training-collaborators-status-${option.code}`"
                  class="flex items-center justify-start py-2 px-4 rounded-lg w-full"
                  :class="
                    draftStatus === option.code
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07'
                  "
                  @click="toggleStatus(option.code)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
            <div>
              <h4 class="font-medium text-gray-07 pb-4">
                Cargo
              </h4>
              <SearchCombobox
                :model-value="draftPositionId"
                :options="positionOptions"
                :loading="isPositionsLoading"
                placeholder="Selecione um cargo"
                search-placeholder="Buscar cargo..."
                class="w-full min-w-70"
                @update:model-value="draftPositionId = $event || ''"
                @search="handlePositionSearch"
              />
            </div>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
